import { Outlet, useLocation } from "react-router-dom";
import { PageHeader } from "../layout/PageHeader";

// The sections live in the sidebar, folded out under Flows, so this only has to
// say where you are. A tab strip here as well would be the same navigation
// twice, and eight tabs wrapped onto two rows on a laptop.

interface Section {
	match: (path: string) => boolean;
	title: string;
	blurb: string;
}

const SECTIONS: Section[] = [
	{
		match: (p) => p.startsWith("/flows/templates"),
		title: "Templates",
		blurb:
			"Start from something that already works. Every template arrives as a draft you own outright — changing it later changes nothing else.",
	},
	{
		match: (p) => p.startsWith("/flows/runs"),
		title: "Runs",
		blurb:
			"Every execution, and what each node put in and took out. A failed run says which node stopped it and why.",
	},
	{
		match: (p) => p.startsWith("/flows/comparisons"),
		title: "Comparisons",
		blurb:
			"Pairs worth an A-versus-B page. Pick the ones you want written; a dismissed pair is never proposed again.",
	},
	{
		match: (p) => p.startsWith("/flows/seo"),
		title: "SEO queue",
		blurb:
			"Which product pages are waiting to be rewritten, why each one is there, and whether the rewrites are working. The queue is filled by the jobs, not from here.",
	},
	{
		match: (p) => p.startsWith("/flows/approvals"),
		title: "Approvals",
		blurb:
			"Runs paused waiting for a person. Approving resumes the run with the items you kept.",
	},
	{
		match: (p) => p.startsWith("/flows/schedule"),
		title: "Schedule",
		blurb: "When every published flow next fires, and when it last did.",
	},
	{
		match: (p) => p.startsWith("/flows/calendar"),
		title: "Calendar",
		blurb:
			"Seasonal themes, shared by every flow. A theme with nothing mapped to it produces nothing.",
	},
	{
		match: (p) => p.startsWith("/flows/settings"),
		title: "Settings",
		blurb:
			"Keys and accounts the nodes use. Secrets are encrypted and never sent back to this page.",
	},
	{
		match: () => true,
		title: "Flows",
		blurb:
			"Draw a journey — pick products, have a model write something, draft it in the CMS, announce it. Nothing reaches the public without a node someone placed on purpose.",
	},
];

export const FlowsLayout = () => {
	const { pathname } = useLocation();
	const section = SECTIONS.find((s) => s.match(pathname)) as Section;

	return (
		<div className="space-y-6">
			<PageHeader
				kicker={
					section.title === "Flows"
						? "Automation · Flows"
						: `Flows · ${section.title}`
				}
				title={`${section.title}.`}
				description={section.blurb}
			/>

			<Outlet />
		</div>
	);
};
